/**
 * 用量反応手法の pros/cons（レガシー DoseResponseView methodOptions 相当）
 */

import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

type DrMethod = 'probit' | 'logistic' | 'reed-muench' | 'spearman-karber';

const METHOD_ADV_KEYS: Record<DrMethod, string[]> = {
  logistic: [
    'keisuke.doseResponse.methodInfo.logistic.advantages.0',
    'keisuke.doseResponse.methodInfo.logistic.advantages.1',
    'keisuke.doseResponse.methodInfo.logistic.advantages.2',
    'keisuke.doseResponse.methodInfo.logistic.advantages.3',
    'keisuke.doseResponse.methodInfo.logistic.advantages.4',
    'keisuke.doseResponse.methodInfo.logistic.advantages.5',
  ],
  probit: [
    'keisuke.doseResponse.methodInfo.probit.advantages.0',
    'keisuke.doseResponse.methodInfo.probit.advantages.1',
    'keisuke.doseResponse.methodInfo.probit.advantages.2',
    'keisuke.doseResponse.methodInfo.probit.advantages.3',
    'keisuke.doseResponse.methodInfo.probit.advantages.4',
    'keisuke.doseResponse.methodInfo.probit.advantages.5',
  ],
  'reed-muench': [
    'keisuke.doseResponse.methodInfo.reedMuench.advantages.0',
    'keisuke.doseResponse.methodInfo.reedMuench.advantages.1',
    'keisuke.doseResponse.methodInfo.reedMuench.advantages.2',
    'keisuke.doseResponse.methodInfo.reedMuench.advantages.3',
  ],
  'spearman-karber': [
    'keisuke.doseResponse.methodInfo.spearmanKarber.advantages.0',
    'keisuke.doseResponse.methodInfo.spearmanKarber.advantages.1',
    'keisuke.doseResponse.methodInfo.spearmanKarber.advantages.2',
    'keisuke.doseResponse.methodInfo.spearmanKarber.advantages.3',
    'keisuke.doseResponse.methodInfo.spearmanKarber.advantages.4',
  ],
};

const METHOD_DIS_KEYS: Record<DrMethod, string[]> = {
  logistic: [
    'keisuke.doseResponse.methodInfo.logistic.disadvantages.0',
    'keisuke.doseResponse.methodInfo.logistic.disadvantages.1',
    'keisuke.doseResponse.methodInfo.logistic.disadvantages.2',
  ],
  probit: [
    'keisuke.doseResponse.methodInfo.probit.disadvantages.0',
    'keisuke.doseResponse.methodInfo.probit.disadvantages.1',
    'keisuke.doseResponse.methodInfo.probit.disadvantages.2',
    'keisuke.doseResponse.methodInfo.probit.disadvantages.3',
  ],
  'reed-muench': [
    'keisuke.doseResponse.methodInfo.reedMuench.disadvantages.0',
    'keisuke.doseResponse.methodInfo.reedMuench.disadvantages.1',
    'keisuke.doseResponse.methodInfo.reedMuench.disadvantages.2',
    'keisuke.doseResponse.methodInfo.reedMuench.disadvantages.3',
  ],
  'spearman-karber': [
    'keisuke.doseResponse.methodInfo.spearmanKarber.disadvantages.0',
    'keisuke.doseResponse.methodInfo.spearmanKarber.disadvantages.1',
    'keisuke.doseResponse.methodInfo.spearmanKarber.disadvantages.2',
    'keisuke.doseResponse.methodInfo.spearmanKarber.disadvantages.3',
    'keisuke.doseResponse.methodInfo.spearmanKarber.disadvantages.4',
  ],
};

const METHOD_SUITABLE_KEY: Record<DrMethod, string> = {
  logistic: 'keisuke.doseResponse.methodInfo.logistic.suitable',
  probit: 'keisuke.doseResponse.methodInfo.probit.suitable',
  'reed-muench': 'keisuke.doseResponse.methodInfo.reedMuench.suitable',
  'spearman-karber': 'keisuke.doseResponse.methodInfo.spearmanKarber.suitable',
};

interface DoseResponseMethodInfoPanelProps {
  method: string;
}

export default function DoseResponseMethodInfoPanel({ method }: DoseResponseMethodInfoPanelProps) {
  const { t } = useTranslation();
  if (!(method in METHOD_ADV_KEYS)) return null;
  const m = method as DrMethod;

  return (
    <Accordion flush className="mt-2">
      <Accordion.Item eventKey="dr-method-info">
        <Accordion.Header>{t('keisuke.doseResponse.methodInfoTitle')}</Accordion.Header>
        <Accordion.Body className="small">
          <p className="fw-semibold mb-1">{t('keisuke.doseResponse.advantages')}</p>
          <ul className="mb-2">
            {METHOD_ADV_KEYS[m].map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
          <p className="fw-semibold mb-1">{t('keisuke.doseResponse.disadvantages')}</p>
          <ul className="mb-2">
            {METHOD_DIS_KEYS[m].map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
          <p className="mb-0 text-muted">{t(METHOD_SUITABLE_KEY[m])}</p>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}
